import { 
  Box, 
  Button, 
  Grid, 
  MenuItem, 
  Select, 
  TextField, 
  useTheme,
  InputAdornment,
  alpha,
  styled,
  SelectChangeEvent
} from '@mui/material';
import { Environment } from '../../environment';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';

interface IDadosSelect {
  nameBD: string;
  descricao: string;
}

interface IFerramentasDaListagemProps {
  dadosSelecao?: IDadosSelect[];
  textoDaBusca?: string;
  textoDoSelect?: string;
  mostrarInputBusca?: boolean;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
  aoMudarTextoDoSelect?: (novoTextoselect: string) => void;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  mostrarInputBasicSelect?: boolean;
  aoClicarEmNovo?: () => void;
}

// Componente estilizado para o Select - agora tipado com string
const StyledSelect = styled(Select<string>)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
}));

// Componente estilizado para o TextField
const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
}));

export const FerramentasDaListagemCadastros: React.FC<IFerramentasDaListagemProps> = ({
  textoDaBusca = '',
  textoDoSelect = '',
  aoMudarTextoDeBusca,
  aoMudarTextoDoSelect,
  mostrarInputBusca = false,
  mostrarInputBasicSelect = false,
  aoClicarEmNovo,
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
  dadosSelecao = [],
}) => {
  const theme = useTheme();

  // Função para tratar mudança no Select
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    aoMudarTextoDoSelect?.(event.target.value);
  };
   
  return ( 
    <Box
      flexWrap="wrap"
      gap={1}
      marginX={1}
      marginBottom={2}
      padding={2}
      display="flex"
      alignItems="center"
      borderRadius={2}
      boxShadow={1}
      sx={{
        backgroundColor: 'background.paper',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {/* Filtro/Select */}
        {mostrarInputBasicSelect && (
          <Grid item xs={12} sm={6} md={3} lg={3} xl={2}>
            <Box>
              <StyledSelect
                fullWidth
                size="small" 
                value={textoDoSelect}
                onChange={handleSelectChange}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <FilterListIcon fontSize="small" color="action" />
                  </InputAdornment>
                }
              >
                {dadosSelecao.map((item) => (
                  <MenuItem key={item.nameBD} value={item.nameBD}>
                    {item.descricao}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
          </Grid>
        )}
        
        {/* Campo de busca */}
        {mostrarInputBusca && (
          <Grid item xs={12} sm={6} md={5} lg={4} xl={4}>
            <Box>
              <StyledTextField
                fullWidth
                size="small"
                value={textoDaBusca}
                placeholder={Environment.INPUT_DE_BUSCA}
                onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        )}
        
        {/* Espaço flexível que empurra o botão para a direita */}
        <Grid item xs style={{ display: mostrarBotaoNovo ? 'block' : 'none' }}>
          <Box sx={{ display: { xs: 'none', md: 'block' } }} />
        </Grid>
        
        {/* Botão Novo - sempre alinhado à direita */}
        {mostrarBotaoNovo && (
          <Grid item xs={12} sm={12} md="auto" lg="auto" xl="auto">
            <Box display="flex" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Button
                color="primary"
                disableElevation
                variant="contained"
                onClick={aoClicarEmNovo}
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium',
                  px: 3,
                  py: 1,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }
                }}
              >
                {textoBotaoNovo}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};